import QRCode from 'qrcode';
import { resolveConfirmationVenueDetails } from '../components/AssetGetByIdView';
import type { AppLanguage } from '../i18n/types';
import { registeredAssetVenueById } from './assetThumbnailStore';
import {
  bookingCalendarEventInputFromParts,
  type BookingCalendarEventInput,
} from './bookingCalendarExport';

export type BookingConfirmationLabels = {
  title: string;
  subtitle: string;
  scheduleLabel: string;
  peopleLabel: string;
  vehicleLabel?: string;
  bookingIdLabel: string;
  showBarcode: string;
  hideBarcode: string;
  qrCaption: string;
  venueLoading?: string;
  venueUnknown?: string;
  loading?: string;
};

export type BookingConfirmationDetails = {
  bookingId: string;
  assetName: string;
  assetAddress: string | null;
  scheduleText: string;
  startDateIso?: string | null;
  endDateIso?: string | null;
  amountOfPeople: number | null;
  /** When false, guest count is hidden (private office, conference room, etc.). */
  showPeopleCount?: boolean;
  /** Selected parking vehicle summary line (manufacturer · model · plate). */
  vehicleSummary?: string | null;
  /** True while venue name/address are being enriched from the asset API. */
  venuePending?: boolean;
  language?: AppLanguage;
  labels: BookingConfirmationLabels;
};

export function bookingConfirmationPeopleCount(
  amountOfPeople: number | null,
): number | null {
  if (typeof amountOfPeople !== 'number' || !Number.isFinite(amountOfPeople)) {
    return null;
  }
  return Math.max(0, Math.floor(amountOfPeople));
}

export function resolveBookingConfirmationVenue(
  assetId: string | null | undefined,
  sources: Array<Record<string, unknown> | null | undefined>,
  language: AppLanguage,
): { assetName: string; assetAddress: string | null } {
  const merged: Array<Record<string, unknown> | null | undefined> = [];
  const trimmedId = typeof assetId === 'string' ? assetId.trim() : '';
  if (trimmedId) {
    merged.push(registeredAssetVenueById(trimmedId));
  }
  merged.push(...sources);
  return resolveConfirmationVenueDetails(merged, language);
}

export function bookingConfirmationDisplayPeopleCount(
  details: BookingConfirmationDetails,
): number | null {
  if (details.showPeopleCount !== true) {
    return null;
  }
  return bookingConfirmationPeopleCount(details.amountOfPeople);
}

export function bookingCalendarEventFromConfirmation(
  details: BookingConfirmationDetails,
): BookingCalendarEventInput | null {
  return bookingCalendarEventInputFromParts({
    assetName: details.assetName,
    startDateIso: details.startDateIso,
    endDateIso: details.endDateIso,
    assetAddress: details.assetAddress,
    scheduleText: details.scheduleText,
    bookingId: details.bookingId,
    peopleCount: bookingConfirmationDisplayPeopleCount(details),
  });
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function confirmationHtml(details: BookingConfirmationDetails, qrDataUrl: string): string {
  const dir = details.language === 'he' ? 'rtl' : 'ltr';
  const lang = details.language === 'he' ? 'he' : 'en';
  const peopleCount = bookingConfirmationDisplayPeopleCount(details);
  const peopleRow =
    peopleCount !== null
      ? `<div class="row"><dt>${escapeHtml(details.labels.peopleLabel)}</dt><dd>${peopleCount}</dd></div>`
      : '';
  const vehicleSummary = details.vehicleSummary?.trim();
  const vehicleRow =
    vehicleSummary && details.labels.vehicleLabel
      ? `<div class="row"><dt>${escapeHtml(details.labels.vehicleLabel)}</dt><dd dir="ltr">${escapeHtml(vehicleSummary)}</dd></div>`
      : '';
  const venueTitle =
    details.assetName.trim() ||
    (details.venuePending ? details.labels.venueLoading : details.labels.venueUnknown) ||
    '';
  const addressLine = details.assetAddress
    ? `<p class="address">${escapeHtml(details.assetAddress)}</p>`
    : '';
  const venueHeader = `<header class="venueHeader">
    <p class="assetName">${escapeHtml(venueTitle)}</p>
    ${addressLine}
  </header>`;
  const qrSection = qrDataUrl
    ? `<button type="button" class="showBarcode" id="show-barcode-btn">${escapeHtml(details.labels.showBarcode)}</button>
    <div class="qrBlock" id="qr-block" hidden>
      <img src="${qrDataUrl}" width="220" height="220" alt="" />
      <p class="qrCaption">${escapeHtml(details.labels.qrCaption)}</p>
      <button type="button" class="hideBarcode" id="hide-barcode-btn">${escapeHtml(details.labels.hideBarcode)}</button>
    </div>
    <script>
      (function () {
        var showBtn = document.getElementById('show-barcode-btn');
        var hideBtn = document.getElementById('hide-barcode-btn');
        var block = document.getElementById('qr-block');
        showBtn?.addEventListener('click', function () {
          if (block) { block.hidden = false; }
          this.style.display = 'none';
        });
        hideBtn?.addEventListener('click', function () {
          if (block) { block.hidden = true; }
          if (showBtn) { showBtn.style.display = ''; }
        });
      })();
    </script>`
    : '';

  return `<!DOCTYPE html>
<html lang="${lang}" dir="${dir}">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(details.labels.title)}</title>
  <style>
    * { box-sizing: border-box; }
    body {
      margin: 0;
      padding: 2rem 1.25rem 2.5rem;
      font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
      background: #e8edf3;
      color: #0f172a;
      line-height: 1.45;
    }
    .card {
      position: relative;
      max-width: 22rem;
      margin: 2rem auto 0;
      padding: 2.2rem 1.25rem 1.5rem;
      background: #fff;
      border-radius: 14px;
      border: 1px solid #e2e8f0;
      box-shadow: 0 18px 40px rgba(15, 23, 42, 0.12);
      text-align: center;
    }
    .venueHeader {
      margin: 0 0 0.85rem;
      text-align: center;
    }
    .assetName { margin: 0; font-size: 1rem; font-weight: 700; }
    .address {
      margin: 0.3rem 0 0;
      font-size: 0.88rem;
      font-weight: 500;
      color: #475569;
      line-height: 1.35;
    }
    h1 {
      margin: 0 0 0.45rem;
      font-size: 1.2rem;
      font-weight: 800;
      color: #0b4f9c;
    }
    .subtitle { margin: 0 0 1rem; font-size: 0.88rem; color: #334155; }
    dl {
      margin: 0 0 1rem;
      padding: 0.75rem 0.85rem;
      border-radius: 10px;
      background: #f8fafc;
      text-align: start;
    }
    .row {
      display: grid;
      grid-template-columns: minmax(4.5rem, 38%) 1fr;
      gap: 0.25rem 0.55rem;
      padding: 0.35rem 0;
    }
    .row + .row { border-top: 1px solid #e8eef6; }
    dt { margin: 0; font-size: 0.72rem; font-weight: 700; color: #64748b; }
    dd { margin: 0; font-size: 0.9rem; font-weight: 600; word-break: break-word; }
    .id { font-size: 0.78rem; color: #0b4f9c; }
    .showBarcode,
    .hideBarcode {
      border: none;
      background: transparent;
      color: #0b4f9c;
      font: inherit;
      font-size: 1rem;
      font-weight: 700;
      cursor: pointer;
    }
    .hideBarcode {
      display: block;
      margin: 0.65rem auto 0;
      font-size: 0.88rem;
    }
    .qrBlock { margin-top: 0.5rem; text-align: center; }
    .qrBlock img {
      width: 11rem;
      height: 11rem;
      border-radius: 8px;
      border: 1px solid #d8e2f0;
    }
    .qrCaption { margin: 0.55rem 0 0; font-size: 0.78rem; color: #64748b; }
    @media print {
      body { background: #fff; padding: 0; }
      .card { box-shadow: none; border: none; max-width: none; }
      #show-barcode-btn { display: none !important; }
      #qr-block { display: block !important; }
    }
  </style>
</head>
<body>
  <main class="card">
    ${venueHeader}
    <h1>${escapeHtml(details.labels.title)}</h1>
    <p class="subtitle">${escapeHtml(details.labels.subtitle)}</p>
    <dl>
      <div class="row">
        <dt>${escapeHtml(details.labels.scheduleLabel)}</dt>
        <dd>${escapeHtml(details.scheduleText)}</dd>
      </div>
      ${peopleRow}
      ${vehicleRow}
      <div class="row">
        <dt>${escapeHtml(details.labels.bookingIdLabel)}</dt>
        <dd class="id">${escapeHtml(details.bookingId)}</dd>
      </div>
    </dl>
    ${qrSection}
  </main>
</body>
</html>`;
}

/** Call synchronously from a click handler so the browser allows the popup. */
export function openBlankBookingConfirmationWindow(loadingLabel: string): Window | null {
  const win = window.open('about:blank', '_blank');
  if (!win) {
    return null;
  }
  win.document.write(
    `<!DOCTYPE html><html><head><meta charset="utf-8" /><title>${escapeHtml(loadingLabel)}</title></head><body style="font-family:system-ui,sans-serif;padding:2rem;color:#334155;"><p>${escapeHtml(loadingLabel)}</p></body></html>`,
  );
  win.document.close();
  return win;
}

export async function writeBookingConfirmationWindow(
  win: Window,
  details: BookingConfirmationDetails,
): Promise<void> {
  let qrDataUrl = '';
  try {
    qrDataUrl = await QRCode.toDataURL(details.bookingId, {
      width: 220,
      margin: 1,
      errorCorrectionLevel: 'M',
    });
  } catch {
    qrDataUrl = '';
  }
  win.document.open();
  win.document.write(confirmationHtml(details, qrDataUrl));
  win.document.close();
}

/** Opens a printable confirmation page in a new browser tab/window. */
export async function openBookingConfirmationWindow(
  details: BookingConfirmationDetails,
): Promise<boolean> {
  const win = openBlankBookingConfirmationWindow(details.labels.loading ?? '…');
  if (!win) {
    return false;
  }
  await writeBookingConfirmationWindow(win, details);
  return true;
}
